import { NgModule } from '@angular/core';
import { ExtraOptions, PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './guard/auth.guard';

/**
 * `AuthGuard` va en TODAS las rutas menos las tres publicas.
 *
 * Antes estaba solo en `path: ''`, asi que las otras 18 se abrian sin sesion: alcanzaba
 * con escribir `/order-detail?id=<id>` o `/banking` en la barra de direcciones, o con
 * seguir un deep link. Las reglas de Firestore niegan los datos sin sesion, asi que no
 * habia fuga, pero la pantalla se abria igual —vacia o rota— y `venue-profile` dejaba
 * intentar escribir. Es el bug #5 que mitter-driver-app cerro igual.
 *
 * Las publicas son las tres a las que se llega justamente por NO tener sesion.
 */
export const routes: Routes = [
  {
    path: '',
    loadChildren: () => import('./pages/tabs/tabs.module').then((m) => m.TabsPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'tabs',
    loadChildren: () => import('./pages/tabs/tabs.module').then((m) => m.TabsPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'login',
    loadChildren: () => import('./pages/login/login.module').then((m) => m.LoginPageModule)
  },
  {
    path: 'register',
    loadChildren: () => import('./pages/register/register.module').then((m) => m.RegisterPageModule)
  },
  {
    path: 'reset',
    loadChildren: () => import('./pages/reset/reset.module').then((m) => m.ResetPageModule)
  },

  {
    path: 'orders',
    loadChildren: () => import('./pages/orders/orders.module').then((m) => m.OrdersPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'analytics',
    loadChildren: () => import('./pages/analytics/analytics.module').then((m) => m.AnalyticsPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'account',
    loadChildren: () => import('./pages/account/account.module').then((m) => m.AccountPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'venue-profile',
    loadChildren: () => import('./pages/venue-profile/venue-profile.module').then((m) => m.VenueProfilePageModule),
    canActivate: [AuthGuard]
  },

  {
    path: 'maps',
    loadChildren: () => import('./pages/maps/maps.module').then((m) => m.MapsPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'foods',
    loadChildren: () => import('./pages/foods/foods.module').then((m) => m.FoodsPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'add-new-foods',
    loadChildren: () => import('./pages/add-new-foods/add-new-foods.module').then((m) => m.AddNewFoodsPageModule),
    canActivate: [AuthGuard]
  },
  {
    //  Las categorías se manejan dentro de Productos desde la etapa 3 del rediseño: un enlace
    //  viejo a `/category` o a `/add-category` (página borrada el 31/10/2026) lleva ahí.
    path: 'category',
    redirectTo: '/tabs/products',
    pathMatch: 'full'
  },
  {
    path: 'add-category',
    redirectTo: '/tabs/products',
    pathMatch: 'full'
  },
  {
    path: 'order-detail',
    loadChildren: () => import('./pages/order-detail/order-detail.module').then((m) => m.OrderDetailPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'storefront',
    loadChildren: () => import('./pages/storefront/storefront.module').then((m) => m.StorefrontPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'tracker',
    loadChildren: () => import('./pages/tracker/tracker.module').then((m) => m.TrackerPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'inbox',
    loadChildren: () => import('./pages/inbox/inbox.module').then((m) => m.InboxPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'select-drivers',
    loadChildren: () => import('./pages/select-drivers/select-drivers.module').then((m) => m.SelectDriversPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'new-order',
    loadChildren: () => import('./pages/new-order/new-order.module').then((m) => m.NewOrderPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'preview',
    loadChildren: () => import('./pages/preview/preview.module').then((m) => m.PreviewPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'reject-order',
    loadChildren: () => import('./pages/reject-order/reject-order.module').then((m) => m.RejectOrderPageModule),
    canActivate: [AuthGuard]
  }
];
/**
 * `canceledNavigationResolution: 'computed'`: si una guardia cancela un «atrás» del navegador
 * o de Android (un `popstate`, que ya cambió la URL), el router vuelve a dejar la URL y el
 * historial como estaban. Con el valor por defecto (`'replace'`) la página seguía a la vista con
 * la URL de la anterior. Lo necesita «¿Salir sin guardar?» del editor de productos.
 */
export const ROUTER_OPTIONS: ExtraOptions = {
  preloadingStrategy: PreloadAllModules,
  canceledNavigationResolution: 'computed'
};

@NgModule({
  imports: [RouterModule.forRoot(routes, ROUTER_OPTIONS)],
  exports: [RouterModule]
})
export class AppRoutingModule {}
